import { setAccommodationV2Sessions } from 'actions/AccommodationSessionActions'
import { createEnquirySessionWithMarkupFallback, withoutDroppedChannelMarkupClaim } from 'lib/accommodation/createEnquirySessionWithMarkupFallback'
import { isAccommodationV2Item } from 'lib/checkout/checkoutUtils'
import { buildEnquirySessionPayloadFromCartItem } from 'lib/accommodation/buildEnquirySessionPayload'
import { arrayToMap } from 'lib/array/arrayUtils'
import { getEnquirySubscriber } from 'luxPlus/selectors/checkout/order'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import type { AppDispatch } from 'src/client/store'

/**
 * Mints enquiry sessions for saved accommodationV2 cart items that do not already
 * have a sessionId. Add-to-cart deliberately omits sessions (2h TTL); checkout
 * creates them on entry from the persisted identity + shopped-price snapshot.
 *
 * Fail-closed: any create failure rejects the whole attempt so callers abort
 * rather than proceeding with a partial set of session ids.
 *
 * @param items The full cart, including experience/transfer children needed to reconstruct bundles.
 */
export async function createAccommodationV2SessionsForCartItems(
  items: Array<App.Checkout.AnyItem>,
  dispatch: AppDispatch,
): Promise<Array<App.Checkout.AnyItem>> {
  const state = dispatch((_action: any, getState: () => App.State) => getState())
  const region = state.geo.currentRegionCode
  const isLuxPlusUser = !!getEnquirySubscriber(state)?.tier
  // Cart items are always new bookings. Callers may run before checkout initialises, so `existingOrder` may be stale.
  const lppMemberPricingEnabled = getIsLuxPlusLPPEnabled(state)

  const sessionlessItems = items.filter(isAccommodationV2Item).filter(item => !item.sessionId)
  if (sessionlessItems.length === 0) return items

  // Validate every snapshot before starting requests, so a malformed bundle cannot leave partial sessions.
  const payloads = sessionlessItems.map(item =>
    buildEnquirySessionPayloadFromCartItem(item, region, isLuxPlusUser, items, lppMemberPricingEnabled),
  )
  const settled = await Promise.allSettled(payloads.map(payload => createEnquirySessionWithMarkupFallback(payload)))

  const created = settled.flatMap(result => (
    result.status === 'fulfilled' ? [result.value] : []
  ))
  const createdSessions = created.map(result => result.session)
  if (createdSessions.length > 0) {
    dispatch(setAccommodationV2Sessions(createdSessions))
  }

  const firstRejection = settled.find(result => result.status === 'rejected')
  if (firstRejection?.status === 'rejected') {
    throw firstRejection.reason
  }

  const createdByItemId = arrayToMap(
    sessionlessItems.map((item, index) => ({ itemId: item.itemId, ...created[index] })),
    entry => entry.itemId,
  )

  // An item whose claim was dropped takes the pricing its session was created at, so a later restore
  // or toggle does not send the same doomed claim again.
  return items.map(item => {
    const result = isAccommodationV2Item(item) ? createdByItemId.get(item.itemId) : undefined
    return result && isAccommodationV2Item(item)
      ? withoutDroppedChannelMarkupClaim({ ...item, sessionId: result.session.id }, result.fallbackPricing)
      : item
  })
}
